import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare, Users, Maximize2, Loader2, ArrowRight
} from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';

export default function ConnectWidget({ expandable, expandPath }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    unreadPersonal: 0,
    unreadGroup: 0
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [chatData, allGroupsData] = await Promise.all([
        apiRequest('/chat/unread-count').catch(() => ({ count: 0 })),
        apiRequest('/groups').catch(() => [])
      ]);

      // All groups are visible to everyone
      const groups = Array.isArray(allGroupsData) ? allGroupsData : [];
      let unreadGroup = groups.reduce((acc, g) => acc + (g.unread_count || 0), 0);

      setStats({
        unreadPersonal: chatData.count || 0,
        unreadGroup: unreadGroup
      });
    } catch (error) {
      console.error('Failed to load connect stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalUnread = stats.unreadPersonal + stats.unreadGroup;

  if (loading) {
    return (
      <Card className="h-full" data-testid="connect-widget">
        <CardContent className="flex items-center justify-center h-32">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full" data-testid="connect-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-blue-600" />
            <CardTitle className="text-base">Connect</CardTitle>
            {totalUnread > 0 && (
              <Badge className="bg-red-500 text-white">{totalUnread}</Badge>
            )}
          </div>
          {expandable && expandPath && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(expandPath)}
              className="h-8 w-8 p-0"
            >
              <Maximize2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Personal Chat */}
        <div 
          className="flex items-center justify-between p-3 bg-blue-50 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors"
          onClick={() => navigate('/chat')}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <MessageSquare className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-slate-900">Personal Chat</p>
              <p className="text-xs text-slate-500">Direct messages</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {stats.unreadPersonal > 0 && (
              <Badge className="bg-red-500 text-white">{stats.unreadPersonal}</Badge>
            )}
            <ArrowRight className="h-4 w-4 text-slate-400" />
          </div>
        </div>

        {/* Group Chat */}
        <div 
          className="flex items-center justify-between p-3 bg-purple-50 rounded-lg cursor-pointer hover:bg-purple-100 transition-colors"
          onClick={() => navigate('/groups')}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
              <Users className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="font-medium text-slate-900">Group Chat</p>
              <p className="text-xs text-slate-500">Team discussions</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {stats.unreadGroup > 0 && (
              <Badge className="bg-red-500 text-white">{stats.unreadGroup}</Badge>
            )}
            <ArrowRight className="h-4 w-4 text-slate-400" />
          </div>
        </div>

        {/* No unread state */}
        {totalUnread === 0 && (
          <p className="text-center text-xs text-slate-400 pt-1">
            All caught up! No unread messages.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
